import { createFileRoute, Link } from '@tanstack/react-router';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ludolio/shared-ui';
import { ArrowRight, CheckCircle2, FileArchive, Mail, PackageCheck } from 'lucide-react';
import { PublicDevNav } from '@/components/PublicDevNav';
import { SdkLinks } from '@/components/SdkLinks';
import { useAuthStore } from '@/stores/authStore';
import { GAME_PLATFORMS } from '@/types';

export const Route = createFileRoute('/guide')({
  component: GuidePage,
});

function GuidePage() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  return (
    <div className="min-h-screen bg-[hsl(var(--color-background))] text-[hsl(var(--color-foreground))]">
      <PublicDevNav isAuthenticated={isAuthenticated} />

      <main id="main-content" className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8 lg:py-14">
        <section className="overflow-hidden rounded-[2rem] border border-[hsl(var(--color-primary))]/18 bg-[radial-gradient(circle_at_82%_10%,hsla(21,80%,48%,0.28),transparent_24%),linear-gradient(135deg,hsl(var(--color-card)),hsl(var(--color-background))_55%,hsl(var(--color-card)))] p-6 shadow-2xl sm:p-10">
          <div className="grid gap-8 lg:grid-cols-[1fr_0.55fr] lg:items-end">
            <div>
              <h1 className="max-w-3xl text-3xl font-black leading-[1.4] text-[hsl(var(--color-foreground))] sm:text-6xl">
                How to distribute your game with Ludolio
              </h1>
              <p className="mt-6 max-w-2xl text-base leading-7 text-[hsl(var(--color-muted-foreground))]">
                This guide describes the steps required to create a developer account and submit a game to Ludolio.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Button asChild>
                  <Link to={isAuthenticated ? '/dashboard' : '/signup'}>
                    {isAuthenticated ? 'Open portal' : 'Create developer account'} <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="border-[hsl(var(--color-primary))]/28 bg-[hsl(var(--color-card))]/50 text-[hsl(var(--color-foreground))] hover:bg-[hsl(var(--color-primary))]/12 hover:text-[hsl(var(--color-foreground))]">
                  <a href="mailto:ben@ludolio.com?subject=Game%20distribution%20inquiry">
                    Contact us <Mail className="h-4 w-4" />
                  </a>
                </Button>
              </div>
            </div>

            <div className="rounded-2xl border border-[hsl(var(--color-muted-foreground))]/[0.12] bg-[hsl(var(--color-background))]/[0.32] p-5 backdrop-blur">
              <p className="text-sm font-bold uppercase tracking-[0.22em] text-[hsl(var(--color-primary-text))]">Before you begin</p>
              <ul className="mt-5 space-y-4 text-sm text-[hsl(var(--color-muted-foreground))]">
                {['A playable build per target OS', 'Icon, banner, and store copy', "Achievements and stats configuration", 'Choice of SDK-integrated or DRM-free'].map((item) => (
                  <li key={item} className="flex gap-3">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--color-primary))]" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <div className="mt-8 grid gap-5 lg:grid-cols-[0.64fr_0.36fr]">
          <div className="space-y-5">
            <Card className="border-[hsl(var(--color-primary))]/16 bg-[hsl(var(--color-card))] text-[hsl(var(--color-foreground))]">
              <CardHeader>
                <CardTitle>Quickstart</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <ol className="list-decimal space-y-3 pl-6 text-sm leading-6 text-[hsl(var(--color-muted-foreground))]">
                  <li>Create a developer account.</li>
                  <li>Tell us about your game, and get your account verified (required to submit a game).</li>
                  <li>Upload your build as a ZIP file with its version, target platform, and executable path.</li>
                  <li>Add your game details.</li>
                  <li>If integrating our SDK, configure your achievements and stats.</li>
                  <li>Test the uploaded game through the Ludolio client.</li>
                </ol>
              </CardContent>
            </Card>

            <Card className="border-[hsl(var(--color-primary))]/16 bg-[hsl(var(--color-card))] text-[hsl(var(--color-foreground))]">
              <CardHeader>
                <CardTitle>SDK Integration</CardTitle>
                <CardDescription className="text-[hsl(var(--color-muted-foreground))]">Developers are given the choice of submitting their game as SDK-integrated or DRM-free.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm leading-6 text-[hsl(var(--color-muted-foreground))]">
                <ul className="list-disc space-y-2 pl-6">
                  <li><span className="font-semibold text-[hsl(var(--color-foreground))]">SDK-integrated</span>: enables platform features such as achievements, stats, and secure sessions.</li>
                  <li><span className="font-semibold text-[hsl(var(--color-foreground))]">DRM-free</span>: your game launches without session validation. This option does not allow us to manage digital rights, monitor stats or achievements.</li>
                </ul>
                <SdkLinks className="border-t border-[hsl(var(--color-primary))]/16 pt-4" />
              </CardContent>
            </Card>

            <Card className="border-[hsl(var(--color-primary))]/16 bg-[hsl(var(--color-card))] text-[hsl(var(--color-foreground))]">
              <CardHeader>
                <CardTitle>Build packaging and upload requirements</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm leading-6 text-[hsl(var(--color-muted-foreground))]">
                <ul className="list-disc space-y-2 pl-6">
                  <li>Build files must be uploaded as <span className="font-semibold text-[hsl(var(--color-foreground))]">.zip</span> archives.</li>
                  <li>Provide the <span className="font-semibold text-[hsl(var(--color-foreground))]">relative path</span> to the executable inside the zip.</li>
                  <li>Uploaded builds are kept private until you publish them.</li>
                </ul>

                <div className="rounded-xl border border-[hsl(var(--color-primary))]/16 bg-[hsl(var(--color-background))]/[0.36] p-4">
                  <p className="mb-2 font-semibold text-[hsl(var(--color-foreground))]">Executable path examples</p>
                  <ul className="list-disc space-y-1 pl-6">
                    <li>Windows: <code className="text-[hsl(var(--color-primary-text))]">MyGame.exe</code></li>
                    <li>macOS app bundle: <code className="text-[hsl(var(--color-primary-text))]">MyGame.app</code></li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            <Card id="testing" className="border-[hsl(var(--color-primary))]/16 bg-[hsl(var(--color-card))] text-[hsl(var(--color-foreground))]">
              <CardHeader>
                <CardTitle>Testing expectations</CardTitle>
                <CardDescription className="text-[hsl(var(--color-muted-foreground))]">Before submitting, you should validate your build.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm leading-6 text-[hsl(var(--color-muted-foreground))]">
                <ul className="list-disc space-y-2 pl-6">
                  <li>Run a local smoke test on every target OS before uploading.</li>
                  <li>Check launch, save/load, controller or keyboard support, and common display resolutions.</li>
                  <li>If your game uses the SDK, verify the session flow plus each achievement or stat you configured.</li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <aside className="space-y-5 lg:sticky lg:top-20 lg:self-start">
            <Card className="border-[hsl(var(--color-primary))]/32 bg-[hsl(var(--color-primary))]/12 text-[hsl(var(--color-foreground))]">
              <CardHeader>
                <PackageCheck className="mb-2 h-6 w-6 text-[hsl(var(--color-primary))]" />
                <CardTitle>Submission readiness</CardTitle>
                <CardDescription className="text-[hsl(var(--color-muted-foreground))]">We will require the following details before publishing your game.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-[hsl(var(--color-muted-foreground))]">
                {['Complete metadata', 'Age ratings', 'System requirements', 'Accessibility notes', 'At least one tested build'].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="h-4 w-4 text-[hsl(var(--color-primary))]" />
                    {item}
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="border-[hsl(var(--color-primary))]/16 bg-[hsl(var(--color-card))] text-[hsl(var(--color-foreground))]">
              <CardHeader>
                <FileArchive className="mb-2 h-6 w-6 text-[hsl(var(--color-muted-foreground))]" />
                <CardTitle>Supported platforms</CardTitle>
                <CardDescription className="text-[hsl(var(--color-muted-foreground))]">Upload one build per platform.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  {GAME_PLATFORMS.map((p) => (
                    <div key={p.value} className="flex items-center justify-between rounded-lg border border-[hsl(var(--color-primary))]/14 bg-[hsl(var(--color-muted))]/[0.045] px-3 py-2">
                      <span className="font-medium text-[hsl(var(--color-foreground))]/85">{p.label}</span>
                      <code className="text-xs text-[hsl(var(--color-muted-foreground))]">{p.value}</code>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

          </aside>
        </div>
      </main>
    </div>
  );
}